import { useLingui } from "@lingui/react";
import { Box, Stack, Switch, Typography } from "@mui/material";
import { differenceInDays } from "date-fns";
import Counter from "@/components/Counter";
import { useBookingContext } from "@/contexts/BookingContext";
import type { Extra } from "@/types/api";
import { getExtraPeriodLabel, isNoPriceBillingType } from "@/utils/extras";
import { formatCurrency } from "@/utils/pricing";

interface ExtraItemProps {
  extra: Extra;
  price: number | null;
  priceAlt: number | null;
  selectedAmount: number;
  onToggle: (extra: Extra) => void;
  onAmountChange: (extra: Extra, amount: number) => void;
  currency: string;
  currencyAlt: string | undefined;
}

export default function ExtraItem({
  extra,
  price,
  priceAlt,
  selectedAmount,
  onToggle,
  onAmountChange,
  currency,
  currencyAlt,
}: ExtraItemProps) {
  const { i18n } = useLingui();
  const { state } = useBookingContext();

  const isSelected = selectedAmount > 0;
  const isFreeType = extra.extra_type === "free";
  const billingType = extra.billing_type;

  const nights = differenceInDays(state.dateUntil!, state.dateFrom!);

  const handleClick = () => {
    if (extra.extra_type !== "free") {
      onToggle(extra);
    }
  };

  const handleAmountChange = (newAmount: number) => {
    const clampedAmount = Math.max(0, Math.min(99, newAmount));
    onAmountChange(extra, clampedAmount);
  };

  return (
    <Box
      onClick={handleClick}
      sx={{
        display: "flex",
        alignItems: "top",
        gap: 2,
      }}
    >
      <Switch
        checked={isSelected}
        onChange={() => onToggle(extra)}
        onClick={(e) => e.stopPropagation()}
      />

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, flex: 1 }}>
        <Box>
          <Typography variant="body1" sx={{ fontWeight: 600, mb: 0.5 }}>
            {extra.name}
          </Typography>

          {extra.description && (
            <Typography
              variant="caption"
              color="text.secondary"
              // biome-ignore lint/security/noDangerouslySetInnerHtml: HTML content from trusted backend API
              dangerouslySetInnerHTML={{ __html: extra.description }}
              sx={{ "& p": { m: 0 }, mt: 0.5, display: "block" }}
            />
          )}
          {isFreeType && (
            <Counter
              count={selectedAmount}
              onChange={handleAmountChange}
              min={0}
              max={99}
              sx={{ mt: 1 }}
            />
          )}
        </Box>
      </Box>

      <Box>
        <Box sx={{ textAlign: "right", ml: 2, minWidth: 100 }}>
          {isNoPriceBillingType(billingType) ? (
            <Typography color="primary.main" sx={{ fontWeight: 600 }}>
              {getExtraPeriodLabel(billingType, i18n, nights)}
            </Typography>
          ) : (
            <>
              <Stack
                direction={{ xs: "column", md: "row" }}
                spacing={1}
                justifyContent="end"
                useFlexGap
              >
                {priceAlt && (
                  <Typography color="text.secondary" component="span" order={{ xs: 2, md: 1 }}>
                    ({formatCurrency(priceAlt, currencyAlt!, i18n.locale)})
                  </Typography>
                )}
                <Typography
                  color="primary.main"
                  sx={{ fontWeight: 600 }}
                  component="span"
                  order={{ xs: 1, md: 2 }}
                >
                  {formatCurrency(price ?? 0, currency, i18n.locale)}
                </Typography>
              </Stack>

              {billingType && (
                <Typography
                  variant="caption"
                  color="text.secondary"
                  sx={{ display: "block", mt: 0.5 }}
                >
                  {getExtraPeriodLabel(billingType, i18n, nights)}
                </Typography>
              )}
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
}
